import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Select,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

import { AppContext } from "../../App";
import * as actions from "../../api/actions/pos";
import { endpoints, controller } from "../../api/actions/api";
import { Alert } from "../../layout";
import { api } from "../../api/actions/api";
import { formatCurrency } from "../../hooks/Currency";
import ReportPrintButton from "../reports/ReportPrintButton";


const CustomerAccount = (props) => {
  const { t } = useTranslation();
  const {
    _customersList,
    _getById,
    _authUser,
    _store,
  } = props;

  const { setLoader } = useContext(AppContext);

  const [customerId, setCustomerId] = useState("");
  const [account, setAccount] = useState(null);
  const reportRef = useRef(null);

  const [selectedSale, setSelectedSale] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentType, setPaymentType] = useState(1);
  const [paymentNotes, setPaymentNotes] = useState("");

  const [alert, setAlert] = useState({
    isOpen: false,
    type: "",
    title: "",
    subTitle: "",
  });

  //------------------------------------------------
  // Load Customers
  //------------------------------------------------
  useEffect(() => {
  // Clear customer/account selected from previous store
  setCustomerId("");
  setAccount(null);
  setSelectedSale(null);

  if (_store?.id) {
    setLoader(true);

    _getById(
      endpoints.Customer + controller.GetByStore,
      _store.id,
      actions.ActionType.SetCustomers,
      () => setLoader(false)
    );
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [_store]);

  //------------------------------------------------
  // Load Customer Account
  //------------------------------------------------
  const loadAccount = (id) => {
    if (!id) {
        setAccount(null);
        return;
    }

    setLoader(true);

    api(
        `${endpoints.CustomerPayment}${controller.GetCustomerAccount}/${id}`
    )
        .get()
        .then((res) => {
        setLoader(false);

        if (res?.data?.status === 200) {
            setAccount(res.data.result);
        } else {
            setAccount(null);

            setAlert({
            isOpen: true,
            type: "error",
            title: t("unableToLoadAccount"),
            subTitle: res?.data?.msg || t("requestFailed"),
            });
        }
        })
        .catch((err) => {
        setLoader(false);
        setAccount(null);

        setAlert({
            isOpen: true,
            type: "error",
            title: t("unableToLoadAccount"),
            subTitle: err.message || t("requestFailed"),
        });
        });
    };

  //------------------------------------------------
  // Save payment for selected customer sale
  // ------------------------------------------------
    const savePayment = () => {
    if (!selectedSale) return;

    const amount = Number(paymentAmount || 0);

    if (!Number.isFinite(amount) || amount <= 0) {
        setAlert({
        isOpen: true,
        type: "warning",
        title: t("invalidAmount"),
        subTitle: t("paymentAmountGreaterThanZero"),
        });
        return;
    }

    if (amount > Number(selectedSale.balance)) {
        setAlert({
        isOpen: true,
        type: "warning",
        title: t("invalidAmount"),
        subTitle: t("paymentExceedsBalance", { balance: formatCurrency(selectedSale.balance, _store),}),
        });
        return;
    }

    setLoader(true);

    api(endpoints.CustomerPayment)
        .post({
        customerId: Number(customerId),
        sellId: selectedSale.id,
        storeId: _store.id,
        payType: Number(paymentType),
        amount: amount,
        userId: _authUser.id,
        notes: paymentNotes,
        })
        .then((res) => {
        setLoader(false);

        if (res?.data?.status === 201) {
            setSelectedSale(null);
            setPaymentAmount("");
            setPaymentNotes("");

            loadAccount(customerId);

            setAlert({
            isOpen: true,
            type: "success",
            title: t("paymentSaved"),
            subTitle:
                res.data.msg || t("paymentRecordedSuccessfully"),
            });
        } else {
            setAlert({
            isOpen: true,
            type: "error",
            title: t("unableToSavePayment"),
            subTitle: res?.data?.msg || t("requestFailed"),
            });
        }
        })
        .catch((err) => {
        setLoader(false);

        setAlert({
            isOpen: true,
            type: "error",
            title: t("unableToSavePayment"),
            subTitle: err.message || t("requestFailed"),
        });
        });
    };


  //------------------------------------------------
  // Customer Selection
  //------------------------------------------------
  const handleCustomerChange = (e) => {
    const id = e.target.value;

    setCustomerId(id);
    setAccount(null);
    setSelectedSale(null);
    setPaymentAmount("");
    setPaymentNotes("");

    loadAccount(id);
  };
  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <>
      <Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 2,
          }}
        >
          <Typography variant="h5">
            {t("customerAccount")}
          </Typography>

          {account && (
            <ReportPrintButton
              reportRef={reportRef}
              paperSize="Letter"
              reportTitle={t("customerAccount")}
            />
          )}
        </Box>

        {/* Customer Selection */}
        <Card sx={{ mb: 2 }}>
          <CardContent>

            <Typography variant="h6" mb={1}>
              {t("selectCustomer")}
            </Typography>

            <Select
              fullWidth
              value={customerId}
              onChange={handleCustomerChange}
              displayEmpty
            >
              <MenuItem value="">
                {t("selectCustomer")}
              </MenuItem>

              {_customersList?.map((customer) => (
                <MenuItem
                  key={customer.id}
                  value={customer.id}
                >
                  {customer.customerName}
                </MenuItem>
              ))}
            </Select>

          </CardContent>
        </Card>

        {/* Account Information */}
        {account && (
          <Box
            ref={reportRef}
            className="customer-account-print"
          >
            {/* Customer Information */}
            <Card sx={{ mb: 2 }}>
              <CardContent>

                <Typography variant="h6">
                  {account.customer.customerName}
                </Typography>

                <Typography>
                  {t("phone")}: {account.customer.phone || ""}
                </Typography>

                <Typography>
                  {t("email")}: {account.customer.email || ""}
                </Typography>

              </CardContent>
            </Card>

            {/* Account Summary */}
            <Grid container spacing={2} mb={2}>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography>
                      {t("totalPurchase")}
                    </Typography>

                    <Typography variant="h5">
                      {formatCurrency(account.summary.totalPurchase, _store)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography>
                      {t("totalPaid")}
                    </Typography>

                    <Typography variant="h5">
                      {formatCurrency(account.summary.totalPaid, _store)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography>
                      {t("currentBalance")}
                    </Typography>

                    <Typography variant="h5">
                      {formatCurrency(account.summary.balance, _store)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {Number(account.summary.credit || 0) > 0 && (
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography>
                      {t("customerCredit")}
                    </Typography>

                    <Typography variant="h5">
                      {formatCurrency(account.summary.credit, _store)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}

            </Grid>

            {/* Unpaid Sales */}
            <Card sx={{ mb: 2 }}>
              <CardContent>

                <Typography variant="h6" mb={2}>
                  {t("unpaidSales")}
                </Typography>

                {account.unpaidSales?.length === 0 ? (
                  <Typography>
                    {t("noUnpaidSales")}
                  </Typography>
                ) : (
                  account.unpaidSales?.map((sale) => (
                    <Box
                      key={sale.id}
                      sx={{
                        borderBottom: "1px solid #ddd",
                        py: 1.5,
                      }}
                    >
                      <Typography>
                        {t("invoice")}: #{sale.id}
                      </Typography>

                      <Typography>
                        {t("date")}:{" "}
                        {new Date(
                          sale.sellDate
                        ).toLocaleDateString()}
                      </Typography>
                      <Typography>
                        {t("total")}: {formatCurrency(sale.totalPay, _store)}
                      </Typography>

                      <Typography>
                        {t("paid")}: {formatCurrency(sale.totalPaid, _store)}
                      </Typography>

                      <Typography>
                        {t("balance")}: {formatCurrency(sale.balance, _store)}
                      </Typography>

                      <Button
                      sx={{ mt: 1, "@media print": { display: "none" } }}
                        className="no-print"
                        onClick={() => {
                                setSelectedSale(sale);
                                setPaymentAmount(sale.balance);
                                setPaymentType(1);
                                setPaymentNotes("");
                            }}
                        >
                        {t("receivePayment")}
                      </Button>

                        {selectedSale?.id === sale.id && (
                            <Box sx={{ mt: 2 }} className="no-print">

                            <Typography variant="subtitle1" mb={1}>
                              {t("remainingBalance")}:{" "}
                              {formatCurrency(sale.balance, _store)}
                            </Typography>

                            <TextField
                                label={t("paymentAmount")}

                                type="number"
                                value={paymentAmount}
                                onChange={(e) => setPaymentAmount(e.target.value)}
                                inputProps={{
                                    min: 0,
                                    max: sale.balance,
                                    step: "0.01",
                                }}
                                size="small"
                                sx={{ mr: 1 }}
                            />
                            <TextField
                                label={t("notes")}
                                value={paymentNotes}
                                onChange={(e) => setPaymentNotes(e.target.value)}
                                size="small"
                                sx={{ mr: 1 }}
                            />

                            <Select
                            value={paymentType}
                            onChange={(e) => setPaymentType(e.target.value)}
                            size="small"
                            sx={{ minWidth: 130 }}
                            >
                            <MenuItem value={1}>{t("cash")}</MenuItem>
                            <MenuItem value={2}>{t("card")}</MenuItem>
                            <MenuItem value={4}>{t("check")}</MenuItem>
                            <MenuItem value={7}>{t("other")}</MenuItem>
                            </Select>

                            <Button
                            variant="contained"
                            size="small"
                            sx={{ ml: 1 }}
                            onClick={savePayment}
                            >
                            {t("savePayment")}
                            </Button>

                            <Button
                                variant="outlined"
                                color="inherit"
                                size="small"
                                sx={{ ml: 1 }}
                                onClick={() => {
                                    setSelectedSale(null);
                                    setPaymentAmount("");
                                    setPaymentNotes("");
                                }}
                                >
                                {t("cancel")}
                            </Button>
                        </Box>
                        )}

                    </Box>
                  ))
                )}

              </CardContent>
            </Card>

            {/* Payment History */}
            <Card>
                <CardContent>

                    <Typography variant="h6" mb={2}>
                    {t("paymentHistory")}
                    </Typography>

                    {account.payments?.length === 0 ? (
                    <Typography>
                        {t("noPaymentsRecorded")}
                    </Typography>
                    ) : (
                    <TableContainer component={Paper}>
                        <Table size="small">

                        <TableHead>
                            <TableRow>
                            <TableCell>{t("date")}</TableCell>
                            <TableCell align="right">{t("amount")}</TableCell>
                            <TableCell>{t("invoice")}</TableCell>
                            <TableCell>{t("payType")}</TableCell>
                            <TableCell>{t("notes")}</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                        {[...(account.payments || [])]
                            .sort(
                            (a, b) =>
                                new Date(b.paymentDate) -
                                new Date(a.paymentDate)
                            )
                            .map((payment) => (
                            <TableRow key={payment.id}>

                                <TableCell>
                                {new Date(
                                    payment.paymentDate
                                ).toLocaleDateString()}
                                </TableCell>

                                <TableCell align="right">
                                 {formatCurrency(payment.amount, _store)}
                                </TableCell>

                                <TableCell>
                                {payment.sellId
                                    ? `#${payment.sellId}`
                                    : t("account")}
                                </TableCell>

                                <TableCell>
                                    {payment.payType === 1 && t("cash")}
                                    {payment.payType === 2 && t("card")}
                                    {payment.payType === 3 && t("stripe")}
                                    {payment.payType === 4 && t("check")}
                                    {payment.payType === 5 && t("payLater")}
                                    {payment.payType === 6 && t("split")}
                                    {payment.payType === 7 && t("other")}
                                </TableCell>

                                <TableCell>
                                {payment.notes || ""}
                                </TableCell>

                            </TableRow>
                            ))}
                        </TableBody>

                        </Table>
                    </TableContainer>
                    )}

                </CardContent>
            </Card>

          </Box>
        )}

      </Box>

      <Alert
        alert={alert}
        setAlert={setAlert}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  _customersList: state.pos.customersList,
  _authUser: state.pos.authUser,
  _store: state.pos.defStore,
});

const mapActionToProps = {
  _getById: actions.getById,
};

export default connect(
  mapStateToProps,
  mapActionToProps
)(CustomerAccount);